// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState } from 'react';
import { useCart } from '@/hooks/useCart';
import { gaCouponApplied } from '../../utils/analytics';

interface CouponInputProps {
  compact?: boolean;
}

const CouponInput = ({ compact = false }: CouponInputProps) => {
  useStoreLanguage();
  const {
    appliedCoupon,
    discount,
    applyCoupon,
    removeCoupon,
    couponLoading,
  } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleApply = async () => {
    try {
      const result = await applyCoupon(couponCode);
      setMessage(result.message);
      setIsSuccess(result.success);
      if (result.success) {
        gaCouponApplied({
          coupon_code: couponCode,
          // discount se leerá del contexto en el siguiente render; aquí usamos undefined
          source: 'checkout',
        });
        setCouponCode('');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (_error) {
      setMessage('Error al aplicar el cupón. Inténtalo de nuevo.');
      setIsSuccess(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleRemove = () => {
    removeCoupon();
    setMessage('');
    setCouponCode('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  if (appliedCoupon) {
    return (
      <div className={`${compact ? '' : 'mb-4'}`}>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-coupon-3-line text-emerald-600"></i>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
                  {tr(appliedCoupon.code)}
                </span>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                  {tr(appliedCoupon.type === 'percentage'
                    ? `-${appliedCoupon.value}%`
                    : `-${appliedCoupon.value}\u20AC`)}
                </span>
              </div>
              <p className="text-xs text-emerald-600 truncate">
                -{tr(discount.toFixed(2).replace('.', ','))}{tr(" € de descuento")}</p>
            </div>
          </div>
          <button
            onClick={handleRemove}
            className="w-7 h-7 flex items-center justify-center text-emerald-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer flex-shrink-0"
            title={tr("Eliminar cupón")}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${compact ? '' : 'mb-4'}`}>
      {tr(!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-forest-600 transition-colors cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-coupon-3-line"></i>
          </div>{tr("¿Tienes un cupón de descuento?")}</button>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder={tr("Código de cupón")}
              disabled={couponLoading}
              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent transition-all uppercase tracking-wider disabled:opacity-50"
              maxLength={20}
            />
            <button
              onClick={handleApply}
              disabled={!couponCode.trim() || couponLoading}
              className="px-4 py-2.5 bg-forest-600 text-white text-sm font-medium rounded-lg hover:bg-forest-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {tr(couponLoading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                'Aplicar'
              ))}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                setCouponCode('');
                setMessage('');
              }}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          {tr(message && (
            <p
              className={`text-xs mt-2 flex items-center gap-1 ${
                isSuccess ? 'text-emerald-600' : 'text-red-500'
              }`}
            >
              <i
                className={
                  isSuccess ? 'ri-check-line' : 'ri-error-warning-line'
                }
              ></i>
              {tr(message)}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
};

export default CouponInput;
